import React, { useContext, useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import {
    Grid,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Button,
    IconButton,
    TextField,
    Paper,
    Drawer,
    Typography,
    Box,
    List,
    ListItem,
    Chip,
    Divider,
    Hidden,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
    Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// Ícones
import FilterListIcon from '@material-ui/icons/FilterList';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';

import { Autocomplete } from '@material-ui/lab';
import UIFormTitle from '../../../UI/Form/Title/UIFormTitle';
import StoreContext from '../../../Store/context';
import useApi from '../../../../utils/useApi';
import UIDialogTitle from '../../../UI/Dialog/Title/UIDialogTitle';
import UITablePagination from '../../../UI/Table/Pagination/UITablePagination';
import { emptyObject, formattedCurrency, formattedDateTime, formatClient } from '../../../../utils/utils';
import UIDialogConfirm from '../../../UI/Dialog/Confirm/UIDialogConfirm';

const useStyles = makeStyles((theme) => ({
    root: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    toolbar: {
        padding: theme.spacing(2),
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`
    },
    listContainer: {
        flexGrow: 1,
        overflowY: 'auto',
        backgroundColor: '#f5f5f5',
        padding: theme.spacing(1),
    },
    mobileCard: {
        backgroundColor: '#fff',
        borderRadius: 8,
        marginBottom: theme.spacing(1),
        padding: theme.spacing(2),
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        cursor: 'pointer',
        borderLeft: '4px solid transparent',
        '&.approved': { borderLeftColor: theme.palette.success.main },
        '&.refused': { borderLeftColor: theme.palette.error.main },
        '&.pending': { borderLeftColor: theme.palette.warning.main },
    },
    drawerPaper: {
        width: '85%',
        maxWidth: 400,
        padding: theme.spacing(2),
    },
    statusChip: {
        height: 24,
        fontSize: '0.75rem',
    },
    infoRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    valueText: {
        fontWeight: 'bold',
        fontSize: '1.1rem',
    }
}));

const TransferSearch = (props) => {
    // Recebe cashierInfo agora via props
    const { usersInfo, mobile, cashierInfo } = props;
    const classes = useStyles();
    const theme = useTheme();
    const { credencials } = useContext(StoreContext);

    // Filtros
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [filters, setFilters] = useState({ closed: true }); // Default filters

    // Paginação
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Ações
    const [modalDetails, setModalDetails] = useState(null);
    const [alertAction, setAlertAction] = useState(null);

    // APIs
    const [transfersLoad, transfersInfo] = useApi({
        method: 'get',
        url: '/cash-registers/transfers',
        params: { closed: '0' }, // Carrega abertos por padrão
    });

    const [acceptLoad, acceptInfo] = useApi({ method: 'patch', url: '/cash-registers/transfers' });
    const [refuseLoad, refuseInfo] = useApi({ method: 'delete', url: '/cash-registers/transfers' });

    function getFullName(id) {
        if (!id) return null;
        const user = usersInfo.data.find((value) => value.id === id);
        const fullName = `${user.name} ${user.lastName || ''}`;
        return fullName;
    }

    useEffect(() => {
        transfersLoad();
    }, []);

    // --- Funções de Ajuda ---
    function getStatusColor(transfer) {
        if (transfer.accept === '1') return 'approved';
        if (transfer.refusedBy) return 'refused'; // Assumindo lógica de recusa
        return 'pending';
    }

    function getStatusLabel(transfer) {
        if (transfer.accept === '1') return <Chip label="Aceita" size="small" style={{ backgroundColor: '#e6fffa', color: '#00796b' }} />;
        // Lógica pode variar conforme API, assumindo pendente se não aceito e não recusado
        return <Chip label="Pendente" size="small" style={{ backgroundColor: '#fff8e1', color: '#f57f17' }} />;
    }

    // Função auxiliar para traduzir ID do caixa para Nome
    const getCashierName = (id) => {
        // Se já vier com nome da API, usa. Se não, procura na lista de caixas
        if (!cashierInfo || !cashierInfo.data || cashierInfo.loading) return id; // Fallback para ID

        const found = cashierInfo.data.find(c => c.id == id);
        return found ? found.name : id;
    }

    // --- Handlers ---
    function handleSearch(values) {
        const params = {
            ...values,
            requestedBy: values.requestedBy && values.requestedBy.id,
            // Mapear outros filtros conforme necessidade
        };
        transfersLoad({ params: emptyObject(params) });
        setFilters(values);
        setPage(0);
        setDrawerOpen(false);
    }

    function handleAction(type) {
        if (!modalDetails) return;

        const api = type === 'accept' ? acceptLoad : refuseLoad;
        const data = type === 'accept'
            ? { id: modalDetails.id, approvedValue: modalDetails.requestedValue, close: '1' }
            : { id: modalDetails.id };

        api({
            data,
            onCompleted: (res) => {
                if (res) {
                    setAlertAction({ type, res });
                    setModalDetails(null);
                    transfersLoad(); // Recarrega lista
                }
            }
        });
    }

    // --- Renderização ---
    return (
        <div className={classes.root}>
            {/* Barra de Ferramentas Fixa */}
            <div className={classes.toolbar}>
                <UIFormTitle title="Transferências" />
                <Box flexGrow={1} />
                <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={() => setDrawerOpen(true)}
                    size="small"
                >
                    Filtros
                </Button>
            </div>

            {/* Área de Lista (Com Scroll) */}
            <div className={classes.listContainer}>
                {transfersInfo.loading && <CircularProgress />}

                {!transfersInfo.loading && transfersInfo.data && transfersInfo.data.length === 0 && (
                    <Typography align="center" style={{ marginTop: 20 }} color="textSecondary">
                        Nenhuma transferência encontrada.
                    </Typography>
                )}

                {/* VISÃO MOBILE: LISTA DE CARDS */}
                {mobile && transfersInfo.data && transfersInfo.data
                    .slice(
                        page * rowsPerPage,
                        rowsPerPage === -1
                            ? undefined
                            : page * rowsPerPage + rowsPerPage,
                    )
                    .map((item) => (
                        <div
                            key={item.id}
                            className={`${classes.mobileCard} ${getStatusColor(item)}`}
                            onClick={() => setModalDetails(item)}
                        >
                            <div className={classes.infoRow}>
                                <Typography variant="caption" color="textSecondary">#{item.id} • {formattedDateTime(item.requestedAt)}</Typography>
                                {getStatusLabel(item)}
                            </div>
                            <div className={classes.infoRow}>
                                <Typography variant="body2">
                                    {/* Uso da função getCashierName */}
                                    <strong>De:</strong> {getCashierName(item.senderName || item.sender)} <br />
                                    <strong>Para:</strong> {getCashierName(item.receiverName || item.receiver)}
                                </Typography>
                                <Typography className={classes.valueText} color="primary">
                                    {formattedCurrency(item.requestedValue)}
                                </Typography>
                            </div>
                            {item.comment && (
                                <Typography variant="caption" style={{ fontStyle: 'italic', marginTop: 4 }}>
                                    "{item.comment}"
                                </Typography>
                            )}
                        </div>
                    ))}

                {/* VISÃO DESKTOP: TABELA */}
                {!mobile && !transfersInfo.loading && (
                    <TableContainer component={Paper} elevation={0}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Data</TableCell>
                                    <TableCell>Origem</TableCell>
                                    <TableCell>Destino</TableCell>
                                    <TableCell>Valor</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transfersInfo.data && transfersInfo.data
                                    .slice(
                                        page * rowsPerPage,
                                        rowsPerPage === -1
                                            ? undefined
                                            : page * rowsPerPage + rowsPerPage,
                                    )
                                    .map((row) => (
                                        <TableRow key={row.id} hover onClick={() => setModalDetails(row)} style={{ cursor: 'pointer' }}>
                                            <TableCell>{row.id}</TableCell>
                                            <TableCell>{formattedDateTime(row.requestedAt)}</TableCell>
                                            {/* Uso da função getCashierName */}
                                            <TableCell>{getCashierName(row.senderName || row.sender)}</TableCell>
                                            <TableCell>{getCashierName(row.receiverName || row.receiver)}</TableCell>
                                            <TableCell><strong>{formattedCurrency(row.requestedValue)}</strong></TableCell>
                                            <TableCell>{getStatusLabel(row)}</TableCell>
                                            <TableCell align="center">
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setModalDetails(row); }}>
                                                    <SearchIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>

            {/* Paginação */}
            <Box p={1} bgcolor="#fff" borderTop="1px solid #ddd">
                <UITablePagination
                    count={transfersInfo.data ? transfersInfo.data.length : 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={(e, p) => setPage(p)}
                    onChangeRowsPerPage={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                />
            </Box>

            {/* DRAWER DE FILTROS */}
            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <div className={classes.drawerPaper}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Filtrar</Typography>
                        <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
                    </Box>
                    <Formik initialValues={filters} onSubmit={handleSearch}>
                        {({ submitForm, setFieldValue, values }) => (
                            <Form>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField label="ID" name="id" fullWidth variant="outlined" size="small" onChange={(e) => setFieldValue('id', e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            options={usersInfo.data || []}
                                            getOptionLabel={u => u.name}
                                            onChange={(e, v) => setFieldValue('requestedBy', v)}
                                            renderInput={p => <TextField {...p} label="Solicitado por" variant="outlined" size="small" />}
                                        />
                                    </Grid>
                                    {/* Adicionar DatePicker aqui se necessário */}
                                    <Grid item xs={12}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            onClick={submitForm}
                                        >
                                            Aplicar Filtros
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                </div>
            </Drawer>

            {/* MODAL DE DETALHES E AÇÃO */}
            <Dialog open={!!modalDetails} onClose={() => setModalDetails(null)} fullWidth maxWidth="xs">
                {modalDetails && (
                    <>
                        <UIDialogTitle onClose={() => setModalDetails(null)}>
                            Transferência #{modalDetails.id}
                        </UIDialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="caption">De</Typography>
                                    {/* Uso da função getCashierName */}
                                    <Typography>{getCashierName(modalDetails.senderName || modalDetails.sender)}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption">Para</Typography>
                                    {/* Uso da função getCashierName */}
                                    <Typography>{getCashierName(modalDetails.receiverName || modalDetails.receiver)}</Typography>
                                </Grid>
                                <Grid item xs={12} style={{ textAlign: 'center', margin: '10px 0' }}>
                                    <Typography variant="caption">Valor Solicitado</Typography>
                                    <Typography variant="h4" color="primary">{formattedCurrency(modalDetails.requestedValue)}</Typography>
                                </Grid>
                                <Grid item xs={12}><Divider /></Grid>
                                <Grid item xs={12}>
                                    {/* Uso do formatClient para traduzir o ID do usuário */}
                                    <Typography variant="body2">
                                        <strong>Solicitante:</strong> {getFullName(modalDetails.requestedBy)}
                                    </Typography>
                                    {modalDetails.comment && <Typography variant="body2"><strong>Obs:</strong> {modalDetails.comment}</Typography>}
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            {modalDetails.accept !== '1' && !modalDetails.refusedBy && (
                                <>
                                    <Button
                                        variant="outlined"
                                        style={{ color: theme.palette.error.main, borderColor: theme.palette.error.main }}
                                        startIcon={<CancelIcon />}
                                        onClick={() => handleAction('refuse')}
                                    >
                                        Recusar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<CheckCircleIcon />}
                                        onClick={() => handleAction('accept')}
                                    >
                                        Aceitar
                                    </Button>
                                </>
                            )}
                            {(modalDetails.accept === '1' || modalDetails.refusedBy) && (
                                <Button onClick={() => setModalDetails(null)} color="primary">Fechar</Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* ALERTAS DE CONFIRMAÇÃO */}
            <UIDialogConfirm
                open={!!alertAction}
                close={() => setAlertAction(null)}
                data={alertAction?.type === 'accept' ? acceptInfo : refuseInfo}
                messageSucess={alertAction?.type === 'accept' ? "Transferência aceita!" : "Transferência recusada!"}
            />
        </div>
    );
};

export default TransferSearch;